import type { FC } from 'react';
import { useState, useEffect, memo, useMemo } from 'react';
import { Picture, Tooltip, Rating } from '@ui';
import icons from '@icons';
import { starYellow, starGreyRecipeRating, userNoAvatar } from '@images';
import { createImageErrorHandler } from '@utils/onImageError';
import ReturnProductForm from '@components/Forms/ReturnProductForm';
import LeaveCommentForm from '@components/Forms/LeaveCommentForm';
import API from '@api';
import useWindowSize from '@customHooks/useWindowSize';
import SuccessMessageModal from '@components/Modals/SuccessMessageModal';
import clsx from 'clsx';
import { ReviewabilityStatus } from '@schema/cart';
import './ProductCommentBlock.scss';

const {
  info: { black: infoBlackFilled }
} = icons.Fill;

const onImgError = createImageErrorHandler(userNoAvatar);

interface DisclaimerProps {
  reviewabilityStatus: ReviewabilityStatus;
  changeTabToComments: () => void;
}

const Disclaimer: FC<DisclaimerProps> = memo(
  ({ reviewabilityStatus, changeTabToComments }) => {
    const text = useMemo(() => {
      switch (reviewabilityStatus) {
        case ReviewabilityStatus.NotBought: {
          return (
            <>
              Вы не можете оставить отзыв, так как не покупали у нас данный
              товар.
            </>
          );
        }
        case ReviewabilityStatus.LastOrderReviewed: {
          return (
            <>
              Чтобы оставить <span className='font-medium'>новый отзыв</span>,
              закажите товар еще раз.
            </>
          );
        }
        case ReviewabilityStatus.Reviewable: {
          return null;
        }
        default:
          return null;
      }
    }, [reviewabilityStatus]);

    return (
      <div
        className={`ProductCommentBlock__disclaimer ProductCommentBlock__disclaimer--orange`}
      >
        <Picture icon={infoBlackFilled} height='24' width='24' />
        <p>
          {text}{' '}
          <span className='ProductCommentBlock__disclaimer__medium'>
            Есть вопрос по товару? Задайте его{' '}
            <span
              onClick={changeTabToComments}
              className='ProductCommentBlock__disclaimer__link'
            >
              здесь
            </span>
          </span>
        </p>
      </div>
    );
  }
);

interface ProductCommentBlockProps {
  objectId;
  withReturnForm;
  formPlaceholder;
  onSuccessfullMessageSend;
  isProductAvailableForRefund;
  addCommentInstance;
  commentType;
  productId;
  changeTabToComments;
  user;
  isVesovoi: boolean;
  reviewabilityStatus: ReviewabilityStatus;
}

const ProductCommentBlock: FC<ProductCommentBlockProps> = ({
  objectId,
  withReturnForm,
  formPlaceholder,
  onSuccessfullMessageSend,
  isProductAvailableForRefund,
  addCommentInstance,
  commentType,
  productId,
  changeTabToComments,
  user,
  reviewabilityStatus,
  isVesovoi
}) => {
  const [isAvailableForRefund, setIsAvailableForRefund] = useState(false);
  useEffect(() => {
    setIsAvailableForRefund(isProductAvailableForRefund);
  }, [isProductAvailableForRefund]);
  const { isMobile } = useWindowSize();
  const [rating, setRating] = useState({ value: 0, error: false });
  const [activeForm, setActiveForm] = useState(withReturnForm ? 0 : 2);

  const [isMessageSuccessfullySent, setIsMessageSuccessfullySent] =
    useState(false);
  const openReturnForm = (): void => {
    setActiveForm(1);
  };
  const openCommentForm = (): void => {
    setActiveForm(2);
  };

  const closeForm = (): void => {
    setRating({
      value: 0,
      error: false
    });
    setActiveForm(0);
  };

  const onMessageSend = (): void => {
    setIsMessageSuccessfullySent(true);
    if (onSuccessfullMessageSend) {
      onSuccessfullMessageSend();
      setActiveForm(0);
    }
  };
  const changeFormStateAfterSuccessReturnSubmit = (): void => {
    setActiveForm(0);
    API.getProductInfoTemp({
      id: +productId,
      isMenu: location.pathname.includes('/bar-menu/')
    })
      .then(res => {
        setIsMessageSuccessfullySent(true);
        setIsAvailableForRefund(res.availableForRefund);
      })
      .catch(err => {
        console.log('Error: ', err);
      });
  };
  const getCommentTypeTitle = (): string => {
    let commentTypeTitle = '';
    switch (addCommentInstance) {
      case 'Recipes':
        commentTypeTitle = 'рецепт';
        break;
      case 'Articles':
        commentTypeTitle = 'статью';
        break;
      case 'Catalog':
        commentTypeTitle = 'товар';
        break;
      case 'News':
        commentTypeTitle = 'новость';
        break;
      default:
        commentTypeTitle = '';
    }
    return commentTypeTitle;
  };

  const ratingHandler = (value: number): void => {
    setRating(prev => ({ ...prev, value }));
    if (withReturnForm) {
      const newActiveForm = isAvailableForRefund
        ? value === 0
          ? 0
          : value < 4
          ? 1
          : 2
        : 2;
      setActiveForm(newActiveForm);
    } else {
      if (value && withReturnForm) {
        setActiveForm(2);
      }
    }
  };
  const setRatingErrorTrue = (): void => {
    setRating({ ...rating, error: true });
  };
  const setRatingErrorFalse = (): void => {
    setRating({ ...rating, error: false });
  };
  const onPopupClose = (): void => {
    setIsMessageSuccessfullySent(false);
  };

  const isUserCantSendReview =
    reviewabilityStatus !== ReviewabilityStatus.Reviewable;

  const isShowTabs =
    isAvailableForRefund &&
    !isUserCantSendReview &&
    withReturnForm &&
    rating.value > 0 &&
    rating.value < 4 &&
    withReturnForm;

  if (user.id) {
    return (
      <div className='ProductCommentBlock'>
        <div className='ProductCommentBlock__wrapper'>
          <div className='ProductCommentBlock__left'>
            {/* Блок повторяется для десктопа */}
            {isMobile &&
              reviewabilityStatus !== ReviewabilityStatus.Reviewable &&
              activeForm === 0 &&
              rating.value === 0 &&
              withReturnForm && (
                <Disclaimer
                  reviewabilityStatus={reviewabilityStatus}
                  changeTabToComments={changeTabToComments}
                />
              )}
            {/* --> Блок повторяется для десктопа */}
            {(!isUserCantSendReview || activeForm === 2) && (
              <div className='ProductCommentBlock__user-info'>
                <div className='ProductCommentBlock__avatar-wrapper'>
                  <img
                    onError={onImgError}
                    src={user.mainImage || userNoAvatar}
                    className='ProductCommentBlock__avatar'
                    alt={user.fio}
                  />
                </div>
                <div className='ProductCommentBlock__name-rating'>
                  <div
                    className={`ProductCommentBlock__name${
                      !withReturnForm ? ' ProductCommentBlock__name--no-mb' : ''
                    }`}
                  >
                    {user.fio || 'Пользователь'}
                  </div>
                  {withReturnForm && !isUserCantSendReview && (
                    <div className='ProductCommentBlock__rating-wrapper'>
                      <Tooltip
                        onClose={setRatingErrorFalse}
                        tooltipText='Оцените товар'
                        open={rating.error}
                      >
                        {/* div чтобы тултип появлялся */}
                        <div>
                          <Rating
                            readonly={isUserCantSendReview}
                            initialRating={rating.value}
                            emptySymbol={
                              <Picture
                                className='ProductCommentBlock__rating-star'
                                icon={starGreyRecipeRating}
                                height='20'
                              />
                            }
                            fullSymbol={
                              <Picture
                                className='ProductCommentBlock__rating-star'
                                icon={starYellow}
                                height='20'
                              />
                            }
                            onChange={ratingHandler}
                            size={20}
                          />
                        </div>
                      </Tooltip>
                      <div className='ProductCommentBlock__rating-text'>
                        Оцените {getCommentTypeTitle()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {isShowTabs && (
              <div
                className={clsx(
                  'ProductCommentBlock__btns',
                  !isAvailableForRefund && 'ProductCommentBlock__btns--one-el'
                )}
              >
                {isAvailableForRefund && (
                  <button
                    disabled={!isAvailableForRefund}
                    onClick={openReturnForm}
                    className={`ProductCommentBlock__btn${
                      activeForm === 1 ? ' ProductCommentBlock__btn--red' : ''
                    }`}
                  >
                    Оформить возврат
                  </button>
                )}
                <button
                  disabled={isUserCantSendReview}
                  onClick={openCommentForm}
                  className={`ProductCommentBlock__btn${
                    activeForm === 2 ? ' ProductCommentBlock__btn--red' : ''
                  }`}
                >
                  Оставить отзыв
                </button>
              </div>
            )}

            {/* блок повторяется для мобильного */}
            {/* {!isMobile && <ReturnDisclaimer />} */}
            {!isMobile &&
              reviewabilityStatus !== ReviewabilityStatus.Reviewable &&
              activeForm === 0 &&
              rating.value === 0 &&
              withReturnForm && (
                <Disclaimer
                  reviewabilityStatus={reviewabilityStatus}
                  changeTabToComments={changeTabToComments}
                />
              )}
            {/* --> Блок повторяется для десктопа */}
          </div>
          {activeForm === 1 && isAvailableForRefund && (
            <>
              <ReturnProductForm
                changeFormStateAfterSuccessReturnSubmit={
                  changeFormStateAfterSuccessReturnSubmit
                }
                onSuccessfullMessageSend={onSuccessfullMessageSend}
                productId={productId}
                uniqueId={productId}
                closeForm={closeForm}
                className='ProductCommentBlock__return-form'
                isVesovoi={isVesovoi}
              />
            </>
          )}
          {activeForm === 2 && (
            <LeaveCommentForm
              textAreaPlaceholder={formPlaceholder}
              isMobile={isMobile}
              setRatingErrorTrue={setRatingErrorTrue}
              objectId={objectId}
              onMessageSend={onMessageSend}
              withReturnForm={withReturnForm}
              addCommentInstance={addCommentInstance}
              rating={rating.value}
              commentType={commentType}
              productId={productId}
              uniqueId={user.id}
              closeForm={closeForm}
              maximumFilesQuantity={5}
              className={'ProductCommentBlock__comment-form'}
            />
          )}
          {isMessageSuccessfullySent && objectId && (
            <SuccessMessageModal
              onPopupClose={onPopupClose}
              messageType={
                rating.value <= 3 && activeForm !== 2
                  ? 'Return'
                  : withReturnForm
                  ? 'Review'
                  : commentType && productId
                  ? 'Question'
                  : commentType
              }
            />
          )}
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default ProductCommentBlock;
